import { isTerminalStatus } from '@/entities/prediction';
import type { ReplicateClient } from './client';
import type { ReplicatePrediction } from './types';

export interface PollOptions {
  intervalMs?: number;
  maxIntervalMs?: number;
  backoffFactor?: number;
  signal?: AbortSignal;
  onTick?: (p: ReplicatePrediction) => void;
}

/**
 * Polls a Replicate prediction until it reaches a terminal state.
 * Uses exponential backoff capped at maxIntervalMs.
 */
export async function pollPrediction(
  client: ReplicateClient,
  predictionId: string,
  opts: PollOptions = {},
): Promise<ReplicatePrediction> {
  const startInterval = opts.intervalMs ?? 1500;
  const maxInterval = opts.maxIntervalMs ?? 8000;
  const factor = opts.backoffFactor ?? 1.4;
  let interval = startInterval;

  for (;;) {
    const p = await client.getPrediction(predictionId, opts.signal);
    opts.onTick?.(p);
    if (isTerminalStatus(p.status)) return p;
    await sleep(interval, opts.signal);
    interval = Math.min(maxInterval, Math.round(interval * factor));
  }
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const id = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(id);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}
